import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import { instance as axiosInstance } from 'common/axios/axios';
import './PlaceSearch.css';

const libraries: ("places")[] = ["places"];

const mapContainerStyle = {
    width: '100%',
    height: '100%'
};

const defaultCenter = {
    lat: 37.5665, // Seoul default
    lng: 126.9780
};

interface PlaceResult {
    place_id: string;
    name: string;
    formatted_address: string;
    geometry: {
        location: {
            lat: () => number;
            lng: () => number;
        };
    };
}

// 최근 사용한 장소 타입
interface RecentPlace {
    id: string;
    name: string;
    address: string;
    lat: number;
    lng: number;
    lastUsed: string;
}

export const PlaceSearch = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPlace, setSelectedPlace] = useState<{
        name: string;
        address: string;
        lat: number;
        lng: number;
    } | null>(null);
    const [searchResults, setSearchResults] = useState<PlaceResult[]>([]);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [currentLocation, setCurrentLocation] = useState(defaultCenter);

    // 최근 사용한 장소 (API에서 가져옴)
    const [recentPlaces, setRecentPlaces] = useState<RecentPlace[]>([]);

    // Bottom Sheet 상태
    const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState(false);
    const [dragStartY, setDragStartY] = useState(0);
    const [dragCurrentY, setDragCurrentY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const mapRef = useRef<google.maps.Map | null>(null);
    const searchBoxRef = useRef<HTMLInputElement>(null);
    const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
    const geocoderRef = useRef<google.maps.Geocoder | null>(null);
    const bottomSheetRef = useRef<HTMLDivElement>(null);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        libraries,
        language: 'ko',
        region: 'KR'
    });

    // Log API key (first 10 chars only for security)
    useEffect(() => {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        console.log('API Key (first 10 chars):', apiKey?.substring(0, 10));
        console.log('API Key length:', apiKey?.length);
    }, []);

    // Debug: searchResults 변화 추적
    useEffect(() => {
        console.log('searchResults 변경됨, length:', searchResults.length);
    }, [searchResults]);

    // 수정 모드: 기존 장소 정보 로드
    useEffect(() => {
        const placeInfo = location.state?.placeInfo;
        console.log('PlaceSearch - location.state:', location.state);
        console.log('PlaceSearch - placeInfo:', placeInfo);

        if (placeInfo && placeInfo.lat && placeInfo.lng) {
            console.log('기존 장소 정보 로드:', placeInfo);
            setSelectedPlace({
                name: placeInfo.name,
                address: placeInfo.address,
                lat: placeInfo.lat,
                lng: placeInfo.lng
            });
            setMapCenter({ lat: placeInfo.lat, lng: placeInfo.lng });
        }
    }, [location.state]);

    // Get current location on mount
    useEffect(() => {
        console.log('Default center:', defaultCenter);
        console.log('Initial mapCenter:', mapCenter);

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    console.log('User location found:', pos);
                    setCurrentLocation(pos);
                    setMapCenter(pos);
                },
                (error) => {
                    console.log('Error getting current location, using default Seoul coordinates');
                    console.log('Error details:', error);
                    // Explicitly set to default
                    setCurrentLocation(defaultCenter);
                    setMapCenter(defaultCenter);
                }
            );
        } else {
            console.log('Geolocation not available, using default Seoul coordinates');
            setCurrentLocation(defaultCenter);
            setMapCenter(defaultCenter);
        }
    }, []);

    // 최근 사용한 장소 가져오기
    useEffect(() => {
        const fetchRecentPlaces = async () => {
            try {
                const response = await axiosInstance.get('http://localhost:30080/moi/api/v1/meetings/places/recent', {
                    params: { limit: 10 }
                });

                if (response.data.data) {
                    const places = response.data.data.map((place: any) => ({
                        id: place.placeId,
                        name: place.placeName,
                        address: place.placeAddress,
                        lat: parseFloat(place.latitude),
                        lng: parseFloat(place.longitude),
                        lastUsed: place.lastUsedDate
                    }));
                    setRecentPlaces(places);
                }
            } catch (error) {
                console.error('최근 장소 조회 실패:', error);
                // 실패 시 빈 배열 유지
            }
        };

        fetchRecentPlaces();
    }, []);

    const onMapLoad = useCallback((map: google.maps.Map) => {
        console.log('Map loaded successfully!');
        console.log('Map center at load:', mapCenter);
        console.log('Map zoom:', map.getZoom());
        console.log('Map type:', map.getMapTypeId());

        const mapDiv = map.getDiv();
        console.log('Map div:', mapDiv);
        console.log('Map div size:', {
            width: mapDiv.offsetWidth,
            height: mapDiv.offsetHeight,
            clientWidth: mapDiv.clientWidth,
            clientHeight: mapDiv.clientHeight
        });

        mapRef.current = map;
        placesServiceRef.current = new google.maps.places.PlacesService(map);
        geocoderRef.current = new google.maps.Geocoder();

        // Check for projection (indicates map is ready)
        const checkProjection = () => {
            const projection = map.getProjection();
            console.log('Map projection:', projection);
            if (!projection) {
                console.error('❌ Map projection is null - map may not be fully initialized');
            }
        };
        setTimeout(checkProjection, 500);

        // Add error listener for map tiles
        google.maps.event.addListener(map, 'tilesloaded', () => {
            console.log('✅ Map tiles loaded successfully');
        });

        // Add idle listener to check map state
        google.maps.event.addListenerOnce(map, 'idle', () => {
            console.log('Map is idle and ready');
            console.log('Current center:', map.getCenter()?.toJSON());
            console.log('Current zoom:', map.getZoom());
            console.log('Current bounds:', map.getBounds()?.toJSON());
        });

        // Listen for any errors
        google.maps.event.addListener(map, 'error', (error: any) => {
            console.error('❌ Google Maps Error:', error);
        });

        // Force map to render
        setTimeout(() => {
            google.maps.event.trigger(map, 'resize');
            console.log('Map resize triggered');
            map.setCenter(mapCenter);
            console.log('Map center set to:', mapCenter);
        }, 100);
    }, [mapCenter]);

    const handleSearch = () => {
        if (!searchQuery || !placesServiceRef.current) return;

        const request = {
            query: searchQuery,
            location: new google.maps.LatLng(currentLocation.lat, currentLocation.lng),
            radius: 5000,
        };

        placesServiceRef.current.textSearch(request, (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && results) {
                setSearchResults(results as PlaceResult[]);

                // Auto-select first result and center map
                if (results.length > 0 && results[0].geometry) {
                    const firstResult = results[0] as PlaceResult;
                    const lat = firstResult.geometry.location.lat();
                    const lng = firstResult.geometry.location.lng();

                    setSelectedPlace({
                        name: firstResult.name,
                        address: firstResult.formatted_address,
                        lat,
                        lng
                    });
                    setMapCenter({ lat, lng });
                }
            }
        });
    };

    const handleSelectResult = (result: PlaceResult) => {
        const lat = result.geometry.location.lat();
        const lng = result.geometry.location.lng();

        console.log('장소 선택:', result.name);
        setSelectedPlace({
            name: result.name,
            address: result.formatted_address,
            lat,
            lng
        });
        setMapCenter({ lat, lng });
        setSearchQuery('');
        setSearchResults([]);
        console.log('검색 결과 초기화 완료, searchResults.length:', 0);
    };

    // 최근 사용한 장소 선택
    const handleSelectRecentPlace = (place: RecentPlace) => {
        console.log('최근 장소 선택:', place.name);
        setSelectedPlace({
            name: place.name,
            address: place.address,
            lat: place.lat,
            lng: place.lng
        });
        setMapCenter({ lat: place.lat, lng: place.lng });
        setSearchQuery('');
        setSearchResults([]);
        setIsBottomSheetExpanded(false); // 선택 후 Bottom Sheet 닫기
    };

    // Bottom Sheet 토글
    const toggleBottomSheet = () => {
        setIsBottomSheetExpanded(!isBottomSheetExpanded);
    };

    // Bottom Sheet 드래그 시작
    const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        setDragStartY(clientY);
        setIsDragging(true);
    };

    // Bottom Sheet 드래그 중
    const handleDragMove = (e: React.TouchEvent | React.MouseEvent) => {
        if (!isDragging) return;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        setDragCurrentY(clientY);
    };

    // Bottom Sheet 드래그 종료
    const handleDragEnd = () => {
        if (!isDragging) return;

        const dragDistance = dragCurrentY - dragStartY;
        const threshold = 50; // 50px 이상 드래그 시 상태 변경

        if (isBottomSheetExpanded && dragDistance > threshold) {
            // 확장 상태에서 아래로 드래그 -> 축소
            setIsBottomSheetExpanded(false);
        } else if (!isBottomSheetExpanded && dragDistance < -threshold) {
            // 축소 상태에서 위로 드래그 -> 확장
            setIsBottomSheetExpanded(true);
        }

        setIsDragging(false);
        setDragStartY(0);
        setDragCurrentY(0);
    };

    // 좌표로 주소 가져오기 (Reverse Geocoding)
    const getAddressFromLatLng = (lat: number, lng: number): Promise<string> => {
        return new Promise((resolve) => {
            if (!geocoderRef.current) {
                resolve('');
                return;
            }

            geocoderRef.current.geocode(
                { location: { lat, lng } },
                (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                        resolve(results[0].formatted_address);
                    } else {
                        console.log('Geocoding 실패:', status);
                        resolve('');
                    }
                }
            );
        });
    };

    // 지도 클릭 핸들러
    const handleMapClick = async (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;

        const lat = e.latLng.lat();
        const lng = e.latLng.lng();

        // 주소 가져오기
        const address = await getAddressFromLatLng(lat, lng);

        // 주소에서 장소명 추출 (첫 번째 쉼표 이전 또는 전체)
        const placeName = address.split(',')[0] || '선택한 위치';

        setSelectedPlace({
            name: placeName,
            address: address,
            lat,
            lng
        });
        setMapCenter({ lat, lng });
        setSearchResults([]);
    };

    const handleCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    setMapCenter(pos);
                    setCurrentLocation(pos);

                    // 현재 위치의 주소 가져오기
                    const address = await getAddressFromLatLng(pos.lat, pos.lng);
                    const placeName = address.split(',')[0] || '현재 위치';

                    setSelectedPlace({
                        name: placeName,
                        address: address,
                        lat: pos.lat,
                        lng: pos.lng
                    });
                },
                () => {
                    alert('현재 위치를 가져올 수 없습니다.');
                }
            );
        }
    };

    const handleSelectPlace = () => {
        if (!selectedPlace) {
            alert('장소를 선택해주세요.');
            return;
        }

        // 이전 state를 보존하면서 장소 정보만 업데이트
        const newState = {
            ...location.state,  // 이전 state 보존 (isEditMode, meetingId, placeType 등)
            selectedPlace: selectedPlace.name,
            placeInfo: {
                name: selectedPlace.name,
                address: selectedPlace.address,
                lat: selectedPlace.lat,
                lng: selectedPlace.lng
            }
        };

        // returnTo가 있으면 해당 경로로, 없으면 기본 step3(날짜 및 장소)로 이동
        const returnTo = location.state?.returnTo || '/meeting/create/step3';

        console.log('PlaceSearch -> navigate to:', returnTo);
        console.log('이전 state:', location.state);
        console.log('새로운 state:', newState);

        // 장소 선택 후 PlaceSearch로 돌아갈 필요 없으므로 replace 사용
        navigate(returnTo, { state: newState, replace: true });
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    if (loadError) {
        return (
            <div className="place-search-wrapper">
                <Header title="장소 검색" isSubPage />
                <div className="loading-container">
                    <div>지도를 불러올 수 없습니다. API 키를 확인해주세요.</div>
                </div>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="place-search-wrapper">
                <Header title="장소 검색" isSubPage />
                <div className="loading-container">
                    <div>지도를 불러오는 중...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="place-search-wrapper">
            <Header title="장소 검색" isSubPage />

            {/* Search Bar */}
            <div className="place-search-bar">
                <input
                    ref={searchBoxRef}
                    type="text"
                    className="place-search-bar-input"
                    placeholder="장소명 또는 주소 검색"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                />
                <button className="place-search-bar-icon" onClick={handleSearch}>
                    🔍
                </button>
            </div>

            {/* Bottom Sheet - 최근 사용한 장소 */}
            {!searchQuery && searchResults.length === 0 && recentPlaces.length > 0 && (
                <div
                    ref={bottomSheetRef}
                    className={`recent-places-bottom-sheet ${isBottomSheetExpanded ? 'expanded' : 'collapsed'}`}
                    onTouchMove={handleDragMove}
                    onTouchEnd={handleDragEnd}
                    onMouseMove={handleDragMove}
                    onMouseUp={handleDragEnd}
                    onMouseLeave={handleDragEnd}
                >
                    {/* 드래그 핸들 */}
                    <div
                        className="bottom-sheet-handle"
                        onTouchStart={handleDragStart}
                        onMouseDown={handleDragStart}
                        onClick={toggleBottomSheet}
                    >
                        <div className="handle-bar"></div>
                    </div>

                    {/* 헤더 */}
                    <div
                        className="bottom-sheet-header"
                        onClick={toggleBottomSheet}
                    >
                        <span className="bottom-sheet-title">🕒 최근 사용한 장소</span>
                        <span className="bottom-sheet-count">({recentPlaces.length})</span>
                    </div>

                    {/* 장소 목록 */}
                    <div className="bottom-sheet-content">
                        {recentPlaces.map((place) => (
                            <button
                                key={place.id}
                                className="recent-place-item"
                                onClick={() => handleSelectRecentPlace(place)}
                            >
                                <div className="recent-place-icon">📍</div>
                                <div className="recent-place-info">
                                    <div className="recent-place-name">{place.name}</div>
                                    <div className="recent-place-address">{place.address}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Map Container */}
            <div className="map-container">
                <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={15}
                    onLoad={onMapLoad}
                    onClick={handleMapClick}
                    options={{
                        disableDefaultUI: false,
                        zoomControl: true,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                        gestureHandling: 'greedy',
                        mapTypeId: 'roadmap'
                    }}
                >
                    {selectedPlace && (
                        <Marker
                            position={{ lat: selectedPlace.lat, lng: selectedPlace.lng }}
                            animation={google.maps.Animation.DROP}
                        />
                    )}
                </GoogleMap>

                {/* Current Location Button */}
                <button className="map-current-location-btn" onClick={handleCurrentLocation}>
                    <span className="location-icon">🎯</span>
                </button>
            </div>

            {/* Search Results List */}
            {searchResults.length > 0 && (
                <div className="search-results-list">
                    <div className="search-results-header">
                        검색 결과 ({searchResults.length}개)
                    </div>
                    {searchResults.map((result) => (
                        <button
                            key={result.place_id}
                            className="search-result-item"
                            onClick={() => handleSelectResult(result)}
                        >
                            <div className="result-icon">📍</div>
                            <div className="result-info">
                                <div className="result-name">{result.name}</div>
                                <div className="result-address">{result.formatted_address}</div>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Select Button */}
            <div className="place-search-footer">
                <Button
                    text="이 위치로 선택"
                    onPress={handleSelectPlace}
                    disabled={!selectedPlace}
                />
            </div>
        </div>
    );
};
